import { useState, useRef, useCallback } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  ArrowLeft,
  Eye,
  EyeOff,
  Play,
  Printer,
  Maximize,
  Minimize,
  RefreshCw,
  Timer,
  Check,
  X,
  Target,
  Clock,
  Activity,
  RotateCcw
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, Cell } from "recharts";
import { useFullscreen } from "@/hooks/use-fullscreen";
import MathDisplay from "@/components/MathDisplay";
import ModuleLockedState from "@/components/ModuleLockedState";
import { useModuleStatus } from "@/hooks/use-module-status";
import { useProfile } from "@/hooks/use-profile";
import { useRolePerspective } from "@/hooks/use-role-perspective";

// ─── Drill config type ────────────────────────────────────────────────────────
type Operation = "+" | "-" | "*" | "/";

interface DrillConfig {
  id: string;
  name: string;
  yearGroup: string;
  ageLabel: string;
  category: string;
  operation: Operation;
  /** Lower bound for operand generation */
  minVal: number;
  /** Upper bound for operand generation */
  maxVal: number;
  /** For multiplication tables, restrict the second operand to a specific range */
  tableMin?: number;
  tableMax?: number;
  /** Allow negative answers (for negative number drills) */
  allowNegative?: boolean;
  /** Special mode: squares */
  isSquares?: boolean;
}

// ─── Built-in drill configs, calibrated per year group ───────────────────────
// Numbers get harder each year:
//   Y1: 1–9 (single digit)
//   Y2: 1–20 addition, 2-digit subtraction
//   Y3: up to 100 + times tables 2–5
//   Y4: up to 100 + times tables 6–9, simple division
//   Y5: 4-digit numbers, 12× tables, harder division
//   Y6: mixed tables 1–12, mixed division
//   Y7+: negatives, squares
const DRILL_CONFIGS: DrillConfig[] = [
  // ─── Year 1 · Ages 5–6 ────────────────────────────────────────────────────
  // Single-digit sums and differences, answers always 0–18
  {
    id: "y1-add",
    name: "1-Digit Addition",
    yearGroup: "Year 1",
    ageLabel: "Ages 5–6",
    category: "Addition",
    operation: "+",
    minVal: 1,
    maxVal: 9,
  },
  {
    id: "y1-sub",
    name: "1-Digit Subtraction",
    yearGroup: "Year 1",
    ageLabel: "Ages 5–6",
    category: "Subtraction",
    operation: "-",
    minVal: 1,
    maxVal: 9,
  },

  // ─── Year 2 · Ages 6–7 ────────────────────────────────────────────────────
  // Bridging 10, adding to 100, simple 2-digit differences
  {
    id: "y2-add-20",
    name: "Addition up to 20",
    yearGroup: "Year 2",
    ageLabel: "Ages 6–7",
    category: "Addition",
    operation: "+",
    minVal: 1,
    maxVal: 18,
  },
  {
    id: "y2-add-100",
    name: "Adding Tens (to 100)",
    yearGroup: "Year 2",
    ageLabel: "Ages 6–7",
    category: "Addition",
    operation: "+",
    minVal: 10,
    maxVal: 90,
  },
  {
    id: "y2-sub",
    name: "2-Digit Subtraction",
    yearGroup: "Year 2",
    ageLabel: "Ages 6–7",
    category: "Subtraction",
    operation: "-",
    minVal: 10,
    maxVal: 50,
  },

  // ─── Year 3 · Ages 7–8 ────────────────────────────────────────────────────
  {
    id: "y3-add",
    name: "3-Digit Addition",
    yearGroup: "Year 3",
    ageLabel: "Ages 7–8",
    category: "Addition",
    operation: "+",
    minVal: 100,
    maxVal: 500,
  },
  {
    id: "y3-sub",
    name: "3-Digit Subtraction",
    yearGroup: "Year 3",
    ageLabel: "Ages 7–8",
    category: "Subtraction",
    operation: "-",
    minVal: 100,
    maxVal: 500,
  },
  {
    id: "y3-mul-2to5",
    name: "Times Tables ×2 to ×5",
    yearGroup: "Year 3",
    ageLabel: "Ages 7–8",
    category: "Multiplication",
    operation: "*",
    minVal: 2,
    maxVal: 5,
    tableMin: 1,
    tableMax: 12,
  },

  // ─── Year 4 · Ages 8–9 ────────────────────────────────────────────────────
  {
    id: "y4-mul-6to9",
    name: "Times Tables ×6 to ×9",
    yearGroup: "Year 4",
    ageLabel: "Ages 8–9",
    category: "Multiplication",
    operation: "*",
    minVal: 6,
    maxVal: 9,
    tableMin: 1,
    tableMax: 12,
  },
  {
    id: "y4-mul-10to11",
    name: "Times Tables ×10 and ×11",
    yearGroup: "Year 4",
    ageLabel: "Ages 8–9",
    category: "Multiplication",
    operation: "*",
    minVal: 10,
    maxVal: 11,
    tableMin: 1,
    tableMax: 12,
  },
  {
    id: "y4-div-2to5",
    name: "Division ÷2 to ÷5",
    yearGroup: "Year 4",
    ageLabel: "Ages 8–9",
    category: "Division",
    operation: "/",
    minVal: 2,
    maxVal: 5,
    tableMin: 1,
    tableMax: 12,
  },

  // ─── Year 5 · Ages 9–10 ───────────────────────────────────────────────────
  {
    id: "y5-add",
    name: "4-Digit Addition",
    yearGroup: "Year 5",
    ageLabel: "Ages 9–10",
    category: "Addition",
    operation: "+",
    minVal: 1000,
    maxVal: 5000,
  },
  {
    id: "y5-sub",
    name: "4-Digit Subtraction",
    yearGroup: "Year 5",
    ageLabel: "Ages 9–10",
    category: "Subtraction",
    operation: "-",
    minVal: 1000,
    maxVal: 5000,
  },
  {
    id: "y5-mul-12",
    name: "12× Times Table",
    yearGroup: "Year 5",
    ageLabel: "Ages 9–10",
    category: "Multiplication",
    operation: "*",
    minVal: 12,
    maxVal: 12,
    tableMin: 1,
    tableMax: 12,
  },
  {
    id: "y5-div-6to9",
    name: "Division ÷6 to ÷9",
    yearGroup: "Year 5",
    ageLabel: "Ages 9–10",
    category: "Division",
    operation: "/",
    minVal: 6,
    maxVal: 9,
    tableMin: 1,
    tableMax: 12,
  },

  // ─── Year 6 · Ages 10–11 ──────────────────────────────────────────────────
  {
    id: "y6-mul-mix",
    name: "Mixed Times Tables ×1 to ×12",
    yearGroup: "Year 6",
    ageLabel: "Ages 10–11",
    category: "Multiplication",
    operation: "*",
    minVal: 1,
    maxVal: 12,
    tableMin: 1,
    tableMax: 12,
  },
  {
    id: "y6-div-mix",
    name: "Mixed Division ÷1 to ÷12",
    yearGroup: "Year 6",
    ageLabel: "Ages 10–11",
    category: "Division",
    operation: "/",
    minVal: 1,
    maxVal: 12,
    tableMin: 1,
    tableMax: 12,
  },

  {
    id: "y7-neg-add",
    name: "Negative Number Addition",
    yearGroup: "Year 7+",
    ageLabel: "Ages 11+",
    category: "Negative Numbers",
    operation: "+",
    minVal: -20,
    maxVal: 20,
    allowNegative: true,
  },
  {
    id: "y7-neg-sub",
    name: "Negative Number Subtraction",
    yearGroup: "Year 7+",
    ageLabel: "Ages 11+",
    category: "Negative Numbers",
    operation: "-",
    minVal: -20,
    maxVal: 20,
    allowNegative: true,
  },
  {
    id: "y7-squares",
    name: "Squares (1² to 15²)",
    yearGroup: "Year 7+",
    ageLabel: "Ages 11+",
    category: "Powers",
    operation: "*",
    minVal: 1,
    maxVal: 15,
    isSquares: true,
  },
  {
    id: "y7-mul-large",
    name: "Multiply by Multiples of 10",
    yearGroup: "Year 7+",
    ageLabel: "Ages 11+",
    category: "Multiplication",
    operation: "*",
    minVal: 10,
    maxVal: 90,
    tableMin: 2,
    tableMax: 9,
  },
];

// ─── Question generator ───────────────────────────────────────────────────────
function randInt(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function generateQuestion(config: DrillConfig): { q: string; a: string } {
  // Squares
  if (config.isSquares) {
    const n = randInt(config.minVal, config.maxVal);
    return { q: `\\(${n}^2\\)`, a: `${n * n}` };
  }

  // Multiplication tables (with separate table range)
  if (config.operation === "*" && config.tableMin !== undefined && config.tableMax !== undefined) {
    const multiplier = randInt(config.minVal, config.maxVal);
    const multiplicand = randInt(config.tableMin, config.tableMax);
    // Randomise order so both a×b and b×a are practised
    const [left, right] = Math.random() < 0.5 ? [multiplier, multiplicand] : [multiplicand, multiplier];
    return { q: `\\(${left} \\times ${right}\\)`, a: `${left * right}` };
  }

  // Division (always generates clean integer answers)
  if (config.operation === "/") {
    const divisor = randInt(config.minVal, config.maxVal);
    const quotient = randInt(config.tableMin ?? 1, config.tableMax ?? 12);
    const dividend = divisor * quotient;
    return { q: `\\(${dividend} \\div ${divisor}\\)`, a: `${quotient}` };
  }

  // Addition / Subtraction
  let a = randInt(config.minVal, config.maxVal);
  let b = randInt(config.minVal, config.maxVal);

  if (config.operation === "+") {
    return { q: `\\(${a} + ${b}\\)`, a: `${a + b}` };
  }

  // Subtraction: keep non-negative unless allowNegative
  if (!config.allowNegative) {
    const hi = Math.max(a, b);
    const lo = Math.min(a, b);
    return { q: `\\(${hi} - ${lo}\\)`, a: `${hi - lo}` };
  }

  // Negative Subtraction fix for formatting: add parentheses if b is negative
  // Wait, randInt returns random ints. For `a - b` if b is negative it prints `a - -b` which KaTeX handles ok but isn't great.
  // We'll leave it as is for now:
  return { q: `\\(${a} - ${b < 0 ? `(${b})` : b}\\)`, a: `${a - b}` };
}

// ─── Group configs by year group ──────────────────────────────────────────────
const YEAR_GROUPS = Array.from(new Set(DRILL_CONFIGS.map(c => c.yearGroup)));

// ─── Component ────────────────────────────────────────────────────────────────
export default function Drills() {
  const [, setLocation] = useLocation();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [started, setStarted] = useState(false);
  const [questions, setQuestions] = useState<{ q: string; a: string; configId: string }[]>([]);
  const [showAnswers, setShowAnswers] = useState(false);
  const [questionCount, setQuestionCount] = useState(10);
  
  // Interactive mode states
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [timings, setTimings] = useState<number[]>([]);
  const [isFinished, setIsFinished] = useState(false);
  const [currentInput, setCurrentInput] = useState("");
  const [isWrong, setIsWrong] = useState(false);
  const startTimeRef = useRef<number>(0);

  const drillsContainerRef = useRef<HTMLDivElement>(null);
  const { isFullscreen, toggleFullscreen } = useFullscreen();
  const { profile } = useProfile();
  const { effectiveRole } = useRolePerspective(profile?.id, profile?.role);
  const { status: moduleStatus, loading: moduleStatusLoading } = useModuleStatus("drill");

  const toggleId = (id: string) =>
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );

  /** Generate `questionCount` questions, balanced across selected topics */
  const generateQuestions = useCallback(() => {
    const configs = selectedIds
      .map(id => DRILL_CONFIGS.find(c => c.id === id))
      .filter((c): c is DrillConfig => !!c);

    if (configs.length === 0) return;

    const result: { q: string; a: string; configId: string }[] = [];
    // Round-robin across selected configs so the set is balanced
    for (let i = 0; i < questionCount; i++) {
      const config = configs[i % configs.length];
      const { q, a } = generateQuestion(config);
      result.push({ q, a, configId: config.id });
    }

    // Shuffle the round-robin result
    for (let i = result.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [result[i], result[j]] = [result[j], result[i]];
    }

    setQuestions(result);
    setShowAnswers(false);
  }, [selectedIds, questionCount]);

  const handleStart = () => {
    if (selectedIds.length === 0) return;
    generateQuestions();
    setCurrentQuestionIndex(0);
    setTimings([]);
    setIsFinished(false);
    setCurrentInput("");
    setIsWrong(false);
    setStarted(true);
    startTimeRef.current = Date.now();
  };

  const handleAnswerSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isFinished || questions.length === 0) return;
    
    const currentQ = questions[currentQuestionIndex];
    if (currentInput.trim() === currentQ.a) {
      // Correct!
      const timeTaken = Date.now() - startTimeRef.current;
      setTimings(prev => [...prev, timeTaken]);
      
      if (currentQuestionIndex < questions.length - 1) {
        setCurrentQuestionIndex(prev => prev + 1);
        setCurrentInput("");
        setIsWrong(false);
        startTimeRef.current = Date.now();
      } else {
        setIsFinished(true);
      }
    } else {
      // Wrong
      setIsWrong(true);
      setTimeout(() => setIsWrong(false), 500);
      setCurrentInput("");
    }
  };

  const handlePrint = (withAnswers: boolean) => {
    const prev = showAnswers;
    setShowAnswers(withAnswers);
    setTimeout(() => {
      window.print();
      setTimeout(() => setShowAnswers(prev), 100);
    }, 100);
  };

  const cols = questions.length <= 5
    ? questions.length
    : questions.length <= 8
      ? Math.ceil(questions.length / 2)
      : 5;

  const fsGridStyle: React.CSSProperties = isFullscreen
    ? {
      display: "grid",
      gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
      gap: "clamp(8px, 1.5vw, 24px)",
      flex: 1,
      padding: "clamp(8px, 1.5vw, 20px)",
      alignContent: "stretch",
    }
    : {};

  if (!moduleStatusLoading && moduleStatus && moduleStatus !== "active" && effectiveRole !== "admin") {
    return <ModuleLockedState status={moduleStatus} />;
  }

  const selectedNames = selectedIds
    .map(id => DRILL_CONFIGS.find(c => c.id === id)?.name)
    .filter(Boolean)
    .join(" · ");

  return (
    <div
      ref={drillsContainerRef}
      className={`min-h-screen bg-background ${isFullscreen ? "drills-fullscreen" : ""}`}
    >
      <style>{`
        .drills-fullscreen .katex { font-size: 1em; }
        .drills-fullscreen .drill-q-text { font-size: clamp(1.4rem, 3.2vw, 3rem); line-height: 1.4; font-weight: 600; }
        .drills-fullscreen .drill-a-text { font-size: clamp(1.2rem, 2.8vw, 2.8rem); line-height: 1.4; color: #16a34a; font-weight: 700; }
        .drills-fullscreen .drill-num    { font-size: clamp(1.2rem, 2.5vw, 2.5rem); font-weight: 800; }
        .drill-q-text { font-size: 1.25rem; line-height: 1.5; font-weight: 600; }
        .drill-a-text { font-size: 1.1rem; color: #16a34a; font-weight: 700; line-height: 1.5; }
        .drill-num    { font-size: 1rem; font-weight: 800; }
        @media print {
          body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
          .print-hidden { display: none !important; }
        }
        .drills-fullscreen {
          width: 100vw; height: 100vh; display: flex; flex-direction: column; overflow: hidden;
        }
        .drills-fullscreen .drills-inner {
          display: flex; flex-direction: column; height: 100%; padding: clamp(6px, 1.2vw, 16px);
        }
        .drills-fullscreen .drill-card {
          min-height: 0; height: 100%;
          display: flex; flex-direction: column; align-items: center; justify-content: center;
        }
      `}</style>

      {!started ? (
        <>
        {/* ── Topic Selection ───────────────────────────────────────────────── */}
        <div className="container mx-auto px-4 py-8 max-w-5xl">
          <div className="space-y-6">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => setLocation("/")}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-3xl font-bold font-serif">Numeracy Drills</h1>
                <p className="text-muted-foreground mt-1">
                  Select topics and question count — questions are generated fresh every time
                </p>
              </div>
            </div>

            <Card className="p-6">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-lg font-semibold">Select Topics</h3>
              </div>

              <div className="space-y-8">
                {YEAR_GROUPS.map(yearGroup => {
                  const configs = DRILL_CONFIGS.filter(c => c.yearGroup === yearGroup);
                  const ageLabel = configs[0]?.ageLabel ?? "";
                  const categories = Array.from(new Set(configs.map(c => c.category)));

                  return (
                    <div key={yearGroup}>
                      <div className="flex items-baseline gap-3 mb-4 border-b pb-2">
                        <h3 className="text-lg font-bold">{yearGroup}</h3>
                        <span className="text-sm text-muted-foreground">{ageLabel}</span>
                      </div>

                      <div className="space-y-5">
                        {categories.map(cat => (
                          <div key={cat}>
                            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                              {cat}
                            </h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                              {configs.filter(c => c.category === cat).map(config => {
                                const isSelected = selectedIds.includes(config.id);
                                return (
                                  <Card
                                    key={config.id}
                                    className={`p-3 cursor-pointer hover:shadow-md transition-all border-2 ${
                                      isSelected
                                        ? "border-primary bg-primary/5"
                                        : "border-border hover:border-muted-foreground/40"
                                    }`}
                                    onClick={() => toggleId(config.id)}
                                  >
                                    <div className="flex items-center gap-3">
                                      <Checkbox
                                        className="pointer-events-none shrink-0"
                                        checked={isSelected}
                                      />
                                      <Label className="flex-1 cursor-pointer pointer-events-none font-medium text-sm leading-tight">
                                        {config.name}
                                      </Label>
                                    </div>
                                  </Card>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>

            {/* Question count picker */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-3">Number of Questions</h3>
              <div className="flex gap-2 flex-wrap">
                {[5, 6, 8, 10, 12, 15, 20].map(n => (
                  <Button
                    key={n}
                    variant={questionCount === n ? "default" : "outline"}
                    onClick={() => setQuestionCount(n)}
                    className="w-14"
                  >
                    {n}
                  </Button>
                ))}
              </div>
            </Card>

            {/* Spacer for floating bar */}
            <div className="h-24" />
          </div>
        </div>

        {/* ── Floating Action Bar ── */}
        <div className="fixed bottom-0 left-0 right-0 z-50 flex justify-center pb-5 px-4 pointer-events-none">
            <div className="pointer-events-auto flex items-center gap-3 px-6 py-3 rounded-full border bg-background/80 backdrop-blur-xl shadow-[0_-4px_30px_rgb(0_0_0/0.12)] dark:shadow-[0_-4px_30px_rgb(0_0_0/0.35)] transition-all">
                {selectedIds.length > 0 && (
                    <>
                        <span className="text-sm text-muted-foreground font-medium">
                            {selectedIds.length} selected
                        </span>
                        <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => setSelectedIds([])}
                            className="text-muted-foreground whitespace-nowrap h-9"
                        >
                            Deselect All
                        </Button>
                        <div className="w-px h-6 bg-border" />
                    </>
                )}
                <Button
                    size="lg"
                    onClick={handleStart}
                    disabled={selectedIds.length === 0}
                    className="gap-2 rounded-full px-8 shadow-md"
                >
                    <Play className="h-4 w-4" />
                    Start Drills ({questionCount} Questions)
                </Button>
            </div>
        </div>
      </>
      ) : (
        // ── Interactive Drill Display ───────────────────────────────────────────────
        <div className={isFullscreen ? "drills-inner" : "container mx-auto px-4 py-6 max-w-4xl"}>
          <div className={`flex items-center gap-3 flex-wrap print-hidden mb-6`}>
            {!isFullscreen && (
              <Button variant="ghost" size="icon" onClick={() => setStarted(false)}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
            )}
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-bold font-serif">Numeracy Drills</h1>
              <p className="text-sm text-muted-foreground truncate">{selectedNames}</p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button
                variant="outline"
                onClick={() => toggleFullscreen(drillsContainerRef.current || undefined)}
              >
                {isFullscreen ? (
                  <><Minimize className="h-4 w-4 mr-1" />Exit</>
                ) : (
                  <><Maximize className="h-4 w-4 mr-1" />Fullscreen</>
                )}
              </Button>
            </div>
          </div>

          {!isFinished && questions.length > 0 && (
            <div className="flex-1 flex flex-col items-center justify-center gap-8 py-8">
              <div className="w-full flex items-center justify-between px-4 max-w-2xl">
                <span className="text-sm font-semibold text-muted-foreground">
                  Question {currentQuestionIndex + 1} of {questions.length}
                </span>
                <span className="text-sm font-semibold text-primary flex items-center gap-1">
                  <Timer className="h-4 w-4" />
                  Time Trial
                </span>
              </div>
              
              {/* Progress bar */}
              <div className="w-full max-w-2xl h-2 bg-muted rounded-full overflow-hidden">
                <div 
                  className="h-full bg-primary transition-all duration-300 ease-out"
                  style={{ width: `${(currentQuestionIndex / questions.length) * 100}%` }}
                />
              </div>

              <Card className={`w-full max-w-2xl p-10 flex flex-col items-center justify-center gap-10 shadow-lg border-2 transition-transform duration-200 ${isWrong ? 'border-destructive animate-[shake_0.5s_ease-in-out]' : 'border-primary/20'}`}>
                <style>{`
                  @keyframes shake {
                    0%, 100% { transform: translateX(0); }
                    20%, 60% { transform: translateX(-10px); }
                    40%, 80% { transform: translateX(10px); }
                  }
                `}</style>
                
                <div className="drill-q-text math-serif text-center w-full min-h-[4rem] flex items-center justify-center">
                  <MathDisplay displayMode={true}>{questions[currentQuestionIndex].q}</MathDisplay>
                </div>

                <form onSubmit={handleAnswerSubmit} className="w-full max-w-sm flex flex-col items-center gap-4">
                  <Input
                    type="text"
                    inputMode="numeric"
                    autoFocus
                    value={currentInput}
                    onChange={(e) => setCurrentInput(e.target.value)}
                    className={`text-center text-3xl font-bold h-20 bg-background ${isWrong ? 'ring-2 ring-destructive ring-offset-2' : ''}`}
                    placeholder="?"
                    autoComplete="off"
                  />
                  <Button type="submit" size="lg" className="w-full h-14 text-lg font-semibold rounded-xl">
                    Submit Answer
                  </Button>
                </form>
              </Card>
            </div>
          )}

          {isFinished && (
            <div className="flex-1 flex flex-col gap-8 pb-12">
              <div className="text-center space-y-2">
                <div className="inline-flex items-center justify-center p-3 bg-primary/10 text-primary rounded-full mb-4">
                  <Target className="h-8 w-8" />
                </div>
                <h2 className="text-3xl font-bold font-serif">Drill Complete!</h2>
                <p className="text-muted-foreground">Great job. Here's your performance breakdown.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="p-6 flex flex-col items-center justify-center text-center gap-2 border-2 border-primary/10">
                  <Clock className="h-6 w-6 text-blue-500 mb-1" />
                  <span className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Total Time</span>
                  <span className="text-3xl font-bold">{(timings.reduce((a,b)=>a+b, 0) / 1000).toFixed(1)}s</span>
                </Card>
                <Card className="p-6 flex flex-col items-center justify-center text-center gap-2 border-2 border-primary/10">
                  <Activity className="h-6 w-6 text-green-500 mb-1" />
                  <span className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Average Speed</span>
                  <span className="text-3xl font-bold">{((timings.reduce((a,b)=>a+b, 0) / timings.length) / 1000).toFixed(2)}s</span>
                  <span className="text-xs text-muted-foreground">per question</span>
                </Card>
                <Card className="p-6 flex flex-col items-center justify-center text-center gap-2 border-2 border-primary/10">
                  <Check className="h-6 w-6 text-purple-500 mb-1" />
                  <span className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Accuracy</span>
                  <span className="text-3xl font-bold">100%</span>
                  <span className="text-xs text-muted-foreground">{questions.length} questions</span>
                </Card>
              </div>

              {/* Topic Breakdown */}
              {(() => {
                const topicStats = Object.entries(
                  timings.reduce((acc, time, idx) => {
                    const tId = questions[idx].configId;
                    if (!acc[tId]) acc[tId] = [];
                    acc[tId].push(time);
                    return acc;
                  }, {} as Record<string, number[]>)
                ).map(([id, times]) => {
                  const topicName = DRILL_CONFIGS.find(c => c.id === id)?.name || id;
                  const avgTime = times.reduce((a,b)=>a+b, 0) / times.length;
                  return { name: topicName, avgTime: avgTime / 1000, count: times.length };
                }).sort((a, b) => b.avgTime - a.avgTime); // Sort descending (slowest first = weakness)

                return (
                  <Card className="p-6 md:p-8 border-2 shadow-sm">
                    <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
                      <Activity className="h-5 w-5 text-primary" />
                      Topic Breakdown (Weaknesses)
                    </h3>
                    <div className="h-[300px] w-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={topicStats} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                          <XAxis type="number" unit="s" />
                          <YAxis dataKey="name" type="category" width={150} tick={{ fontSize: 12 }} />
                          <RechartsTooltip 
                            cursor={{ fill: 'var(--muted)' }}
                            formatter={(value: number) => [`${value.toFixed(2)}s`, 'Avg Time']}
                          />
                          <Bar dataKey="avgTime" radius={[0, 4, 4, 0]}>
                            {topicStats.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={index === 0 ? 'hsl(var(--destructive))' : 'hsl(var(--primary))'} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <p className="text-sm text-muted-foreground mt-4 text-center">
                      The red bar indicates your slowest topic, representing an area to focus on.
                    </p>
                  </Card>
                );
              })()}

              <div className="flex justify-center mt-4">
                <Button size="lg" onClick={handleStart} className="gap-2 rounded-full px-8 h-14 text-lg">
                  <RotateCcw className="h-5 w-5" />
                  Play Again
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
